import { ProtectedRoute } from '@app/Auth/ProtectedRoute'
import { FeatureFlagGate } from '@app/FeatureFlag/FeatureFlagGate'
import { FEATURE_FLAGS } from '@app/app-constants'
import { appLink } from '@app/config/appLink'
import { LayerMetadataManagement } from '@app/domains/layers/components/LayerMetadataManagement'
import { SuitabilityCreate } from '@app/domains/suitability/components/SuitabilityCreate'
import { SuitabilityEdit } from '@app/domains/suitability/components/SuitabilityEdit'
import { SuitabilityReadMode } from '@app/domains/suitability/components/SuitabilityReadMode'
import { SuitabilitySettings } from '@app/domains/suitability/components/SuitabilitySettings'
import HomeAboutUs from '@app/modules/Home/AboutUs/HomeAboutUs.page'
import HomeContacts from '@app/modules/Home/Contact/HomeContacts.page'
import DataLayout from '@app/modules/Home/Data/Layouts/DataLayout'
import DataLayerDetailOverview from '@app/modules/Home/Data/components/DataLayerDetailOverview'
import DataLayerDetailScenarios from '@app/modules/Home/Data/components/DataLayerDetailScenarios'
import DataLayerDetailSuitability from '@app/modules/Home/Data/components/DataLayerDetailSuitability'
import DataLayerView from '@app/modules/Home/Data/components/DataLayerView'
import HomeDataUpload from '@app/modules/Home/Data/components/DataUpload'
import DatasetEditPage from '@app/modules/Home/Data/pages/DatasetEdit.page'
import DatasetListPage from '@app/modules/Home/Data/pages/DatasetList.page'
import { HazardReference } from '@app/modules/Home/Data/components/HazardReference'
import DataLayerDetailPage from '@app/modules/Home/Data/pages/DataLayerDetail.page'
import HomeProcess from '@app/modules/Home/Process/HomeProcess.page'
import HomeProjectManagement from '@app/modules/Home/ProjectManagement/HomeProjectManagement.page'
import UseCasesPageTemplate from '@app/modules/HomeV2/pages/UseCases.page'
import UseCasesDataNeeds from '@app/modules/HomeV2/pages/UseCasesDataNeeds.page'
import WelcomePage from '@app/modules/HomeV2/pages/Welcome.page'
import HomeV2FeaturesPage from '@app/modules/HomeV2/pages/features.page'
import HomeV2Layout from '@app/modules/HomeV2/layout/Home.layout'
import UseCasesLearningPathway from '@app/modules/HomeV2/pages/useCasesLearningPathway.page'
import UseCasesOverviewPage from '@app/modules/HomeV2/pages/useCasesOverview.page'
import { Navigate, Outlet, Route } from 'react-router-dom'
import { AdaptationOverviewPage } from '@app/modules/HomeV2/pages/AdaptationOverview.page'
import { AdaptationCycleTemplatePage } from '@app/modules/HomeV2/pages/adaptationTemplate.page'

export default function HomeRoutes() {
  return (
    <>
      <Route element={<HomeV2Layout />}>
        <Route path="home">
          <Route index element={<Navigate to="welcome" replace />} />

          <Route path="welcome" element={<WelcomePage />} />

          <Route path="features" element={<HomeV2FeaturesPage />} />

          <Route path="use-cases">
            <Route index element={<Navigate to="adaptation-planning/overview" replace />} />
            <Route path=":useCaseId" element={<UseCasesPageTemplate />}>
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<UseCasesOverviewPage />} />
              <Route path="learning-pathway" element={<UseCasesLearningPathway />} />
              <Route path="data-needs" element={<UseCasesDataNeeds />} />
            </Route>
          </Route>

          <Route
            path="adaptation-cycle"
            element={
              <FeatureFlagGate
                flag={FEATURE_FLAGS.ADAPTATION_CYCLE}
                fallback={<Navigate to={appLink.homeWelcome.path} />}
              >
                <ProtectedRoute>
                  <Outlet />
                </ProtectedRoute>
              </FeatureFlagGate>
            }
          >
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={<AdaptationOverviewPage />} />
            <Route path=":adaptationCycleId" element={<AdaptationCycleTemplatePage />} />
          </Route>

          {/* Legacy-only routes - TODO: remove once home-v2 is fully implemented */}
          <Route path="project-management" element={<HomeProjectManagement />} />
          <Route path="process" element={<HomeProcess />} />
          <Route path="contact" element={<HomeContacts />} />
          <Route path="about-us" element={<HomeAboutUs />} />

          <Route
            path="data-upload"
            element={
              <FeatureFlagGate
                flag={FEATURE_FLAGS.DATA_UPLOAD}
                fallback={<Navigate to={appLink.homeWelcome.path} />}
              >
                <ProtectedRoute>
                  <HomeDataUpload />
                </ProtectedRoute>
              </FeatureFlagGate>
            }
          />

          <Route
            path="data-upload/update"
            element={
              <FeatureFlagGate
                flag={FEATURE_FLAGS.DATA_UPLOAD}
                fallback={<Navigate to={appLink.homeWelcome.path} />}
              >
                <ProtectedRoute>
                  <DatasetListPage />
                </ProtectedRoute>
              </FeatureFlagGate>
            }
          />

          <Route
            path="data-upload/edit/:id"
            element={
              <FeatureFlagGate
                flag={FEATURE_FLAGS.DATA_UPLOAD}
                fallback={<Navigate to={appLink.homeWelcome.path} />}
              >
                <ProtectedRoute>
                  <DatasetEditPage />
                </ProtectedRoute>
              </FeatureFlagGate>
            }
          />

          <Route
            path="data"
            element={
              <FeatureFlagGate
                flag={FEATURE_FLAGS.RISK_MODULE}
                fallback={<Navigate to={appLink.homeWelcome.path} />}
              >
                <ProtectedRoute>
                  <DataLayout />
                </ProtectedRoute>
              </FeatureFlagGate>
            }
          >
            <Route index element={<DataLayerView tab="all" />} />
            <Route path="elements" element={<DataLayerView tab="elements" />} />
            <Route path="hazards" element={<DataLayerView tab="hazards" />} />
            <Route path="contextual" element={<DataLayerView tab="contextual" />} />
            <Route path="reference" element={<HazardReference />} />
            <Route path="detail/:layerId" element={<DataLayerDetailPage />}>
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<DataLayerDetailOverview />} />
              <Route path="overview/metadata" element={<LayerMetadataManagement />} />
              <Route path="scenarios" element={<DataLayerDetailScenarios />} />
              <Route path="suitability" element={<DataLayerDetailSuitability />}>
                <Route index element={<SuitabilityReadMode />} />
                <Route path=":use_case/edit" element={<SuitabilityEdit />} />
                <Route path="create" element={<SuitabilityCreate />} />
                <Route path="settings" element={<SuitabilitySettings />} />
              </Route>
            </Route>
          </Route>
        </Route>
      </Route>
    </>
  )
}
